import React, { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { SEMANTIC_COLORS, COMPONENT_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type EmptyStateVariant = 'noThreads' | 'noResults' | 'error' | 'myThreads' | 'savedThreads';

interface CorkBoardEmptyStateProps {
  variant: EmptyStateVariant;
  onRetry?: () => void;
  onCreatePost?: () => void;
  onBrowseFeed?: () => void;
}

const VARIANT_ICONS = {
  noThreads: RESIDENTS_SVG_COMPONENTS.BOARD,
  noResults: RESIDENTS_SVG_COMPONENTS.MAGNIFYING_GLASS,
  error: RESIDENTS_SVG_COMPONENTS.WARNING,
  myThreads: RESIDENTS_SVG_COMPONENTS.CONVERSATION,
  savedThreads: RESIDENTS_SVG_COMPONENTS.STAR,
} as const;

const VARIANT_CONFIG: Record<EmptyStateVariant, { title: string; subtitle: string; iconColor: string }> = {
  noThreads: {
    title: 'No posts yet',
    subtitle: 'Be the first to share something with your community!',
    iconColor: SEMANTIC_COLORS.text.muted,
  },
  noResults: {
    title: 'No results found',
    subtitle: 'Try adjusting your filters or search terms.',
    iconColor: SEMANTIC_COLORS.text.muted,
  },
  error: {
    title: 'Something went wrong',
    subtitle: 'We couldn\'t load the feed. Please try again.',
    iconColor: SEMANTIC_COLORS.status.warning,
  },
  myThreads: {
    title: 'You haven\'t posted yet',
    subtitle: 'Your community posts will appear here.',
    iconColor: SEMANTIC_COLORS.text.muted,
  },
  savedThreads: {
    title: 'No saved posts',
    subtitle: 'Posts you bookmark will appear here.',
    iconColor: SEMANTIC_COLORS.text.muted,
  },
};

export const CorkBoardEmptyState: React.FC<CorkBoardEmptyStateProps> = memo(({
  variant,
  onRetry,
  onCreatePost,
  onBrowseFeed,
}) => {
  const config = VARIANT_CONFIG[variant];
  const IconComponent = VARIANT_ICONS[variant];

  return (
    <View style={styles.container} accessibilityRole={variant === 'error' ? 'alert' : undefined}>
      <View style={styles.iconWrapper}>
        <IconComponent width={36} height={36} color={config.iconColor} />
      </View>
      <Text style={styles.title}>{config.title}</Text>
      <Text style={styles.subtitle}>{config.subtitle}</Text>

      {(variant === 'noThreads' || variant === 'myThreads') && onCreatePost && (
        <TouchableOpacity style={styles.ctaButton} onPress={onCreatePost} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Create a post">
          <Text style={styles.ctaText}>Create a Post</Text>
        </TouchableOpacity>
      )}

      {variant === 'error' && onRetry && (
        <TouchableOpacity style={styles.retryButton} onPress={onRetry} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Try again">
          <Text style={styles.retryText}>Try Again</Text>
        </TouchableOpacity>
      )}

      {variant === 'savedThreads' && onBrowseFeed && (
        <TouchableOpacity style={styles.ctaButton} onPress={onBrowseFeed} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel="Browse feed">
          <Text style={styles.ctaText}>Browse Feed</Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  iconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    textAlign: 'center',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  ctaButton: {
    backgroundColor: COMPONENT_COLORS.button.primaryBg,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  ctaText: {
    fontSize: 14,
    fontWeight: '600',
    color: COMPONENT_COLORS.button.primaryText,
  },
  retryButton: {
    borderWidth: 1,
    borderColor: SEMANTIC_COLORS.interactive.primary,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: SEMANTIC_COLORS.interactive.primary,
  },
});
